import { FC, useState } from 'react'
import { Helmet } from 'react-helmet'

import { BigNumber } from 'ethers'

import Tile from '~/components/Tile'
import Typography from '~/components/Typography'
import StakeSelector from '~/components/StakeSelector'
import { PageHeader } from '~/components/PageHeader'
import StakeInfo from '~/components/StakeInfo'
import { MaxUint256 } from '@ethersproject/constants/lib/bignumbers'
import { useEarn } from '~/lib/useEarns'
import { toAmount, toBigNumber } from '~/lib/helpers'
import { EarnStatus } from '~/contracts/useEarnContract'
import { pathToEarnPage } from '~/lib/routes'
import { useNavigate } from 'react-router-dom'
import style from './StakePage.pcss'
import Navigation from '~/components/Navigation'
import { CONFIG } from '~/lib/config'
import { isCorrectDecimals } from '~/lib/isCorrectDecimals'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'

export const LP_FARMING_DOCUMENTATION_HREF =
	'https://docs.symbiosis.finance/liquidity-providers/how-to-stake'

const CANONICAL_URL = `https://rewards.symbiosis.finance/lp-farm/pancake-swap/stake`

const StakePage: FC = () => {
	const { contract, earnType } = useEarn()
	const [pending, setPending] = useState(false)
	const navigate = useNavigate()
	const [rawAmount, setRawAmount] = useState<string>('')

	const isSuccess = contract?.status === EarnStatus.SUCCESS

	let balance
	let needApprove
	let amount: BigNumber | undefined

	if (isSuccess) {
		const amountValue = Number.parseFloat(rawAmount)

		if (
			!Number.isNaN(amountValue) &&
			isCorrectDecimals(rawAmount, contract.token.decimals)
		) {
			amount = toBigNumber(contract.token.decimals, rawAmount)
			needApprove =
				contract.token.allowance.eq(0) ||
				contract.token.allowance.lt(amount)
		}

		balance = toAmount(contract.token.decimals, contract.token.balanceOf)
	}

	const onSubmit = () => {
		setPending(true)

		if (needApprove) {
			contract
				.approve(MaxUint256)
				.then((tx) =>
					tx.wait().then(() => {
						setPending(false)
					}),
				)
				.catch((e) => {
					console.error(e)
					setPending(false)
				})
			return
		}

		contract
			.deposit(amount)
			.catch(console.error)
			.finally(() => {
				contract.refresh()
				setPending(false)
				setRawAmount('')
			})
	}

	let disabled
	let buttonText
	if (!isSuccess) {
		disabled = true
		buttonText = 'Loading...'
	} else if (pending) {
		disabled = true
		buttonText = needApprove ? 'Approving...' : 'Depositing...'
	} else {
		disabled =
			(!needApprove &&
				!(
					amount &&
					amount.gt(0) &&
					amount.lte(contract.token.balanceOf)
				)) ||
			!isCorrectDecimals(rawAmount, contract.token.decimals)
		buttonText = needApprove ? 'Approve' : 'Deposit'
	}

	return (
		<Tile>
			<Helmet>
				<link rel="canonical" href={CANONICAL_URL} />
			</Helmet>
			<Tile.Header>
				<PageHeader
					title="Stake your LP"
					onQuestionClick={() => {
						window.open(LP_FARMING_DOCUMENTATION_HREF, '_blank')
					}}
				/>
				<Navigation />
			</Tile.Header>

			<Tile.Item disabled={pending} className={style.inner}>
				<StakeSelector
					onAmountChange={setRawAmount}
					onDexChange={(earnType) =>
						navigate(pathToEarnPage(earnType, 'stake'))
					}
					earnType={earnType}
					withGetLP
					amount={rawAmount}
					maxAmount={balance}
				/>
				<Typography tag="p" color="gray">
					Balance:{' '}
					<Typography color="inherit">{balance ?? '{??}'}</Typography>
				</Typography>
			</Tile.Item>

			<Tile.Item>
				<StakeInfo />
			</Tile.Item>

			<Tile.Footer>
				<SwitchNetworkButton
					block
					color="black"
					onClick={onSubmit}
					disabled={disabled}
					requiredChainId={CONFIG.earns[earnType].chainId}
				>
					{buttonText}
				</SwitchNetworkButton>
			</Tile.Footer>
		</Tile>
	)
}

export default StakePage
